/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: components/webrtc/WebRTCPhonePanel.tsx
 🧩 Description:
    WebRTC softphone panel orchestrator. Delegates:
    - Floating toggle     → WebRTCToggleButton
    - Panel header/status → WebRTCPanelHeader
    - Idle/dial screen    → WebRTCDialScreen
    - Incoming screen     → WebRTCIncomingScreen
    - Active call screen  → WebRTCActiveCallScreen
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useSipPhone, CallState } from '../../hooks/useSipPhone';
import { useAuthStore } from '../../store/auth';
import { WebRTCToggleButton } from './WebRTCToggleButton';
import { WebRTCPanelHeader } from './WebRTCPanelHeader';
import { WebRTCDialScreen } from './WebRTCDialScreen';
import { WebRTCIncomingScreen } from './WebRTCIncomingScreen';
import { WebRTCActiveCallScreen } from './WebRTCActiveCallScreen';

interface WebRTCPhonePanelProps {
    isOpen: boolean;
    onToggle: () => void;
}

export function WebRTCPhonePanel({ isOpen, onToggle }: WebRTCPhonePanelProps) {
    const { user } = useAuthStore();
    const {
        status, callState, callInfo, heldCallInfo, incomingCall,
        isMuted, isOnHold, hasHeldCall,
        connect, call, answer, hangup, reject, hold, unhold,
        toggleMute, sendDTMF, transfer, attendedTransfer,
        setAutoAnswer: setSipAutoAnswer,
    } = useSipPhone();

    const [dialNumber, setDialNumber] = useState('');
    const [showTransfer, setShowTransfer] = useState(false);
    const [transferNumber, setTransferNumber] = useState('');
    const [callDuration, setCallDuration] = useState(0);
    const [showKeypad, setShowKeypad] = useState(false);
    const [lastDialedNumber, setLastDialedNumber] = useState('');

    const [autoAnswer, setAutoAnswer] = useState(() => localStorage.getItem('webrtc_phone_auto_answer') === 'true');
    const extension = user?.extensionNumber || '';

    // ── Auto-connect ──────────────────────────────────────────
    useEffect(() => {
        if (!extension || status !== 'disconnected') return;
        (async () => {
            try {
                const res = await fetch('/api/v1/users/sip-credentials', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                if (!res.ok) { console.warn('Could not get SIP credentials'); return; }
                const data = await res.json();
                if (data.success && data.credentials) {
                    const c = data.credentials;
                    const host = window.location.hostname;
                    connect({ wsServer: c.wsServer || `wss://${host}/webrtc`, domain: c.domain || host, extension: c.username || extension, password: c.password || '', stunServers: data.stunServers || '', turnServer: data.turnServer || '', turnUsername: data.turnUsername || '', turnPassword: data.turnPassword || '', iceGatheringTimeout: parseInt(data.iceGatheringTimeout) || 500, ringtoneUrl: data.ringtoneUrl || '/audio/mixkit-vintage-telephone-ringtone-1356.wav', autoAnswer });
                }
            } catch (err) { console.error('Failed to fetch SIP credentials:', err); }
        })();
    }, [extension, status, connect, autoAnswer]);

    // ── Call duration ─────────────────────────────────────────
    useEffect(() => {
        if (callState === 'active' && callInfo?.startTime) {
            const iv = setInterval(() => setCallDuration(Math.floor((Date.now() - callInfo.startTime!) / 1000)), 1000);
            return () => clearInterval(iv);
        }
        setCallDuration(0);
    }, [callState, callInfo?.startTime]);

    // ── Helpers ───────────────────────────────────────────────
    const formatDuration = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    const getCallStateText = (s: CallState) => ({ ringing: 'Incoming Call', outgoing: 'Calling...', active: 'In Call', hold: 'On Hold' }[s] ?? 'Ready');

    const toggleAutoAnswer = () => { const v = !autoAnswer; setAutoAnswer(v); setSipAutoAnswer(v); localStorage.setItem('webrtc_phone_auto_answer', String(v)); };
    const handleDigitClick = (d: string) => callState === 'active' ? sendDTMF(d) : setDialNumber((p) => p + d);
    const handleCall = () => { if (dialNumber) { setLastDialedNumber(dialNumber); call(dialNumber); setDialNumber(''); } else if (lastDialedNumber) setDialNumber(lastDialedNumber); };
    const handleTransfer = async () => { if (transferNumber) { await transfer(transferNumber); setShowTransfer(false); setTransferNumber(''); } };

    if (!extension) return null;

    return (
        <>
            <WebRTCToggleButton isOpen={isOpen} status={status} onToggle={onToggle} />

            {/* Side Panel */}
            <div className={`fixed right-0 top-0 h-full w-80 bg-card border-l border-border shadow-2xl z-50 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <WebRTCPanelHeader status={status} extension={extension} autoAnswer={autoAnswer} onToggle={onToggle} onToggleAutoAnswer={toggleAutoAnswer} />

                <div className="p-3 h-[calc(100%-52px)] flex flex-col">
                    {callState === 'idle' && (
                        <WebRTCDialScreen dialNumber={dialNumber} status={status} onDialNumberChange={setDialNumber} onDigitClick={handleDigitClick} onBackspace={() => setDialNumber((p) => p.slice(0, -1))} onCall={handleCall} />
                    )}
                    {callState === 'ringing' && incomingCall && (
                        <WebRTCIncomingScreen callInfo={callInfo} onAnswer={answer} onReject={reject} />
                    )}
                    {callState !== 'idle' && callState !== 'ringing' && (
                        <WebRTCActiveCallScreen
                            callState={callState} callInfo={callInfo} heldCallInfo={heldCallInfo}
                            isMuted={isMuted} isOnHold={isOnHold} hasHeldCall={hasHeldCall}
                            callDuration={callDuration} showTransfer={showTransfer}
                            showKeypad={showKeypad} transferNumber={transferNumber}
                            onTransferNumberChange={setTransferNumber}
                            onSetShowTransfer={setShowTransfer} onSetShowKeypad={setShowKeypad}
                            onToggleMute={toggleMute} onHold={hold} onUnhold={unhold}
                            onTransfer={handleTransfer} onAttendedTransfer={attendedTransfer}
                            onSendDTMF={sendDTMF} onHangup={hangup}
                            formatDuration={formatDuration} getCallStateText={getCallStateText}
                        />
                    )}
                </div>
            </div>

            {/* Overlay */}
            {isOpen && <div className="fixed inset-0 bg-black/20 z-40" onClick={onToggle} />}
        </>
    );
}
